import type { flightEntity } from 'freedom-shared';

import { isDirectFlight } from '@/shared/lib';

import { hasSameAirlineForRT } from './group-direct-flights';
import type { useDirectFlightsStore } from '../model';

/**
 * Extracts airline information from an offer's first segment.
 * Returns null if the segment or airline info is missing.
 * For RT offers, uses the second segment's airlineTitle (matching ResultsPage.vue logic).
 */
export function getOfferAirline(
  offer: flightEntity.types.MappedOffer,
  isRoundTrip?: boolean,
): { iata: string; title: string } | null {
  const firstSegment = offer.flights[0]?.segments[0];
  if (!firstSegment?.airline) {
    return null;
  }

  // For RT, use second segment's airlineTitle (matching ResultsPage.vue logic)
  if (isRoundTrip) {
    const secondSegment = offer.flights[1]?.segments[0];
    if (!secondSegment?.airlineTitle) {
      return null;
    }
    return {
      iata: firstSegment.airline,
      title: secondSegment.airlineTitle,
    };
  }

  // For OW, use first segment's airlineTitle
  if (!firstSegment?.airlineTitle) {
    return null;
  }

  return {
    iata: firstSegment.airline,
    title: firstSegment.airlineTitle,
  };
}

/**
 * Checks if direct flights UI components should be shown for an offer.
 * Validates 4 conditions:
 * 1. hasGroups - Direct flight groups are available
 * 2. isDirectFlight - The offer is a direct flight
 * 3. hasSameAirline - For RT: both flights have the same airline; For OW: airline exists
 * 4. offerInGroups - The offer's airline matches one of the available groups
 */
export function shouldShowDirectFlights(
  offer: flightEntity.types.MappedOffer | undefined,
  store: ReturnType<typeof useDirectFlightsStore>,
  isRoundTrip: boolean,
): boolean {
  // Condition 1: Check if groups are available
  if (!store.hasGroups) {
    return false;
  }

  // Condition 2: Check if offer is a direct flight
  if (!offer || !isDirectFlight(offer)) {
    return false;
  }

  // Condition 3: Check if offer has same airline for both flights (for RT)
  if (isRoundTrip && !hasSameAirlineForRT(offer)) {
    return false;
  }

  // Extract airline from offer
  const offerAirline = getOfferAirline(offer, isRoundTrip);
  if (!offerAirline) {
    return false;
  }

  // Condition 4: Check if offer's airline exists in the groups
  const hasMatchingGroup = store.groups.some(
    group => group.airline.iata === offerAirline.iata,
  );

  return hasMatchingGroup;
}
